import React, { useContext } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  Divider,
  List,
  Typography,
  Skeleton,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { AuthContext } from "../../../contexts/AuthContext";
import { SCHEDULE_MENU } from "../../../utils/data/controlData";

export const SortableScheduleList = ({
  items,
  allItems,
  moveSchedule,
  reorderSchedule,
  DndItemComponent,
  onOpenSchedule,
  canMoveSchedule,
  listSx = {},
  showFullWidth = false,
  hideDragHandle = false,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const schedules = allItems || items || [];

  const handleDragEnd = (event) => {
    if (!canMoveSchedule) return;

    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;

    const oldIndex = schedules.findIndex(
      (schedule) => schedule.sch_id === active.id
    );
    const newIndex = schedules.findIndex(
      (schedule) => schedule.sch_id === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

    moveSchedule(oldIndex, newIndex);

    const fromSchedule = schedules[oldIndex];
    if (fromSchedule && reorderSchedule) {
      reorderSchedule({
        id: fromSchedule.sch_id,
        scheduleTitle: fromSchedule.scheduleTitle,
        scheduleNo: fromSchedule.scheduleNo,
      });
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((schedule) => schedule.sch_id)}
        strategy={verticalListSortingStrategy}
      >
        <List
          sx={{
            p: 0,
            overflowX: "hidden",
            ...listSx,
          }}
          aria-label='Schedule list'
        >
          {items.map((schedule, index) => (
            <DndItemComponent
              key={schedule.sch_id}
              scheduleData={{
                id: schedule.sch_id,
                scheduleTitle: schedule.scheduleTitle,
                scheduleNo: schedule.scheduleNo,
              }}
              index={index}
              schedule={schedule}
              schedules={schedules}
              moveSchedule={moveSchedule}
              reorderSchedule={reorderSchedule}
              onOpenSchedule={onOpenSchedule}
              canMoveSchedule={canMoveSchedule}
              showFullWidth={showFullWidth}
              hideDragHandle={hideDragHandle}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
};

const BaseSchedulesMenu = ({
  schedules,
  allSchedules,
  isFetching,
  onAddSchedule,
  onOpenSchedule,
  moveSchedule,
  reorderSchedule,
  DndItemComponent,
  buttonWrapper: ButtonWrapper = React.Fragment,
  buttonProps = {},
  showFullWidth = false,
  searchMode = false,
}) => {
  const context = useContext(AuthContext);
  const { checkAccess } = context;
  const canEditSchedules = checkAccess("edit", SCHEDULE_MENU);
  const canMoveSchedule = canEditSchedules && !searchMode;

  return (
    <Box
      sx={{
        width: "100%",
        padding: 1,
        bgcolor: "grey.50",
        borderRadius: 1,
      }}
    >
      {canEditSchedules && !searchMode && (
        <ButtonWrapper>
          <Button
            startIcon={<AddCircleIcon />}
            variant='contained'
            color='primary'
            onClick={onAddSchedule}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: "medium",
              fontSize: "0.875rem",
              borderRadius: 1,
              boxShadow: 1,
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: 2,
                bgcolor: "primary.dark",
              },
              ...buttonProps.sx,
            }}
            aria-label='Add new schedule'
            {...buttonProps}
          >
            Add New Schedule
          </Button>
        </ButtonWrapper>
      )}
      {!searchMode && <Divider sx={{ my: 1.5, bgcolor: "grey.200" }} />}
      {isFetching ? (
        <Box
          sx={{
            py: 1,
          }}
          aria-label='Loading schedules'
        >
          {[1, 2, 3].map((key) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Skeleton
                variant='circular'
                width={32}
                height={32}
                sx={{ bgcolor: "grey.200" }}
              />
              <Skeleton
                variant='rectangular'
                height={20}
                width='80%'
                sx={{ borderRadius: 1, bgcolor: "grey.200" }}
              />
            </Box>
          ))}
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 1, display: "block", fontStyle: "italic" }}
          >
            Finding your schedules...
          </Typography>
        </Box>
      ) : schedules && schedules.length > 0 ? (
        <SortableScheduleList
          items={schedules}
          allItems={allSchedules}
          moveSchedule={moveSchedule}
          reorderSchedule={reorderSchedule}
          DndItemComponent={DndItemComponent}
          onOpenSchedule={onOpenSchedule}
          canMoveSchedule={canMoveSchedule}
          showFullWidth={showFullWidth}
          hideDragHandle={searchMode}
        />
      ) : (
        <Box
          sx={{
            py: 2,
            px: 1,
            textAlign: "center",
          }}
        >
          <Typography
            variant='body2'
            color='text.primary'
            sx={{ fontWeight: 500, mb: 0.5 }}
          >
            {searchMode ? "No matching schedules." : "No schedules yet."}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 1, fontSize: "0.875rem" }}
          >
            {searchMode
              ? "Try a schedule name, type, or number."
              : "Create your first schedule to start managing your taxes."}
          </Typography>
          {canEditSchedules && !searchMode && (
            <Button
              size='small'
              variant='outlined'
              onClick={onAddSchedule}
              sx={{
                textTransform: "none",
                borderRadius: 1,
                fontSize: "0.8rem",
              }}
            >
              Create Schedule
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BaseSchedulesMenu;
